import React, { useState, createContext, useContext, useCallback } from 'react';
import classNames from 'classnames';

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  variant: 'underline' | 'pill';
}

const TabsContext = createContext<TabsContextValue | null>(null);

export interface TabsProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: 'underline' | 'pill';
  className?: string;
  children?: React.ReactNode;
}

const Tabs: React.FC<TabsProps> = ({
  value: controlledValue,
  defaultValue = '',
  onChange,
  variant = 'underline',
  className,
  children,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [controlledValue, onChange]
  );

  return (
    <TabsContext.Provider value={{ value, onChange: handleChange, variant }}>
      <div className={classNames('ecme-tabs', className)}>{children}</div>
    </TabsContext.Provider>
  );
};

export interface TabListProps {
  className?: string;
  children?: React.ReactNode;
}

export const TabList: React.FC<TabListProps> = ({ className, children }) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error('TabList must be used within Tabs');

  return (
    <div
      className={classNames(
        'ecme-tab-list',
        { 'ecme-tab-list-underline': context.variant === 'underline' },
        { 'ecme-tab-list-pill': context.variant === 'pill' },
        className
      )}
      role="tablist"
    >
      {children}
    </div>
  );
};

export interface TabNavProps {
  value: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

export const TabNav: React.FC<TabNavProps> = ({
  value,
  disabled = false,
  icon,
  className,
  children,
}) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error('TabNav must be used within Tabs');

  const isActive = context.value === value;

  const handleClick = () => {
    if (!disabled) {
      context.onChange(value);
    }
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      className={classNames(
        'ecme-tab-nav',
        {
          'ecme-tab-nav-active': isActive,
          'ecme-tab-nav-disabled': disabled,
          'ecme-tab-nav-underline': context.variant === 'underline',
          'ecme-tab-nav-pill': context.variant === 'pill',
        },
        className
      )}
      onClick={handleClick}
      disabled={disabled}
    >
      {icon && <span className="ecme-tab-nav-icon">{icon}</span>}
      {children}
    </button>
  );
};

export interface TabContentProps {
  value: string;
  className?: string;
  children?: React.ReactNode;
}

export const TabContent: React.FC<TabContentProps> = ({ value, className, children }) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error('TabContent must be used within Tabs');

  if (context.value !== value) return null;

  return (
    <div className={classNames('ecme-tab-content', className)} role="tabpanel">
      {children}
    </div>
  );
};

export default Tabs;
